import { useEffect, useState } from 'react';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { formatPrice } from '@/shared/utils/format-price';
import {
  packQuantity,
  publicTiers,
  quantityStep,
  tierPriceFor,
  type WholesaleFamily,
} from '@/shared/utils/wholesale-pricing';

/**
 * PRAT-187: quantidade grande sem tocar 300 vezes no "+" (estilo Zé Delivery /
 * iFood). Cartões com as faixas de atacado ("60 un. R$ 2,00/un"), atalhos
 * "+30 un." e a quantidade digitável. A faixa sai da soma da família (sabores)
 * com o que já está na sacola, como a API calcula no pedido.
 */
type Props = {
  family: WholesaleFamily;
  quantity: number;
  /** Unidades da mesma família que já estão na sacola (sem esta linha). */
  unitsInCart: number;
  /** Preço unitário do produto sem atacado (com complementos). */
  basePrice: number;
  onSetQuantity: (quantity: number) => void;
};

const MAX_QUANTITY = 9999;

const percentOff = (base: number, price: number) =>
  base > 0 && price < base ? Math.round(((base - price) / base) * 100) : 0;

export function WholesaleQuantityPicker({
  family,
  quantity,
  unitsInCart,
  basePrice,
  onSetQuantity,
}: Props) {
  const step = quantityStep(family);
  const pack = Number(family.pack_size) || 0;
  const tiers = publicTiers(family);
  const units = unitsInCart + quantity;
  const currentPrice = tierPriceFor(family, units) ?? basePrice;

  // Cartões: a compra mínima (1 un ou a caixa) e cada faixa da família.
  const options = [
    { qty: step, price: tierPriceFor(family, step + unitsInCart) ?? basePrice },
    ...tiers
      .filter(t => Number(t.min_qty) > step)
      .map(t => ({ qty: Number(t.min_qty), price: Number(t.unit_price) })),
  ];
  const bestPrice = Math.min(...options.map(o => o.price));
  // Selecionado = a maior faixa que o total (sacola + este item) já alcança.
  const selectedQty = [...options].reverse().find(o => o.qty <= units)?.qty ?? null;

  const chips = pack > 1 ? [pack, pack * 2, pack * 4] : [6, 12, 24];
  // Sempre no múltiplo da caixa: a mínima que fecha a caixa e, daí, de caixa em caixa.
  const minQty = packQuantity(family, unitsInCart, 1);
  const set = (n: number) =>
    onSetQuantity(Math.min(MAX_QUANTITY, packQuantity(family, unitsInCart, Number(n) || 1)));
  // O que se digita só vale ao sair do campo (ou Enter): digitar "6" a caminho de
  // "60" não pode virar a caixa de 30 no meio.
  const [draft, setDraft] = useState(String(quantity));
  useEffect(() => setDraft(String(quantity)), [quantity]);
  const commitDraft = () => {
    set(Number(draft));
    setDraft(String(packQuantity(family, unitsInCart, Number(draft) || 1)));
  };
  const missingForPack =
    family.pack_only && pack > 1 && units % pack !== 0 ? pack - (units % pack) : 0;
  const firstTier = tiers[0];

  return (
    <section data-testid="wholesale-picker" className="space-y-3">
      <div className="flex items-baseline justify-between gap-2">
        <h2 className="text-base font-bold">Leve + por menos</h2>
        {firstTier && (
          <span className="text-xs text-gray-500">A partir de {firstTier.min_qty} unidades</span>
        )}
      </div>

      <div className="-mx-6 flex gap-2 overflow-x-auto px-6 pb-1 [scrollbar-width:none]">
        {options.map(option => {
          const selected = option.qty === selectedQty;
          const off = percentOff(basePrice, option.price);
          return (
            <button
              key={option.qty}
              type="button"
              data-testid={`wholesale-tier-${option.qty}`}
              onClick={() => set(Math.max(1, option.qty - unitsInCart))}
              className={`relative flex min-w-[148px] shrink-0 flex-col overflow-hidden rounded-2xl border-2 text-left transition-colors ${
                selected ? 'border-gray-900' : 'border-gray-200'
              }`}
            >
              <div className="flex flex-col gap-1 px-3 pt-3 pb-3">
                <div className="flex items-center gap-2">
                  <span className="whitespace-nowrap text-base font-bold">{option.qty} un.</span>
                  {off > 0 && (
                    <span className="rounded-md bg-green-50 px-1.5 py-0.5 text-xs font-bold text-green-700">
                      -{off}%
                    </span>
                  )}
                </div>
                <span
                  className={`text-sm font-semibold ${selected ? 'text-gray-900' : 'text-gray-500'}`}
                >
                  {formatPrice(String(option.price))}/un.
                </span>
              </div>
              {option.price === bestPrice && off > 0 && (
                <span className="mt-auto bg-green-700 py-1 text-center text-xs font-bold text-white">
                  Melhor preço
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex h-12 items-center rounded-2xl border border-gray-300">
        <button
          type="button"
          aria-label="Diminuir"
          onClick={() => set(quantity - step)}
          disabled={quantity <= minQty}
          className="flex h-12 w-12 items-center justify-center text-gray-600 disabled:text-gray-300"
        >
          <MinusIcon className="h-5 w-5" />
        </button>
        <input
          data-testid="wholesale-quantity"
          type="number"
          inputMode="numeric"
          min={minQty}
          step={step}
          max={MAX_QUANTITY}
          value={draft}
          onFocus={e => e.currentTarget.select()}
          onChange={e => setDraft(e.target.value)}
          onBlur={commitDraft}
          onKeyDown={e => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          aria-label="Quantidade"
          className="h-12 min-w-0 flex-1 bg-transparent text-center text-lg font-bold outline-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none"
        />
        <button
          type="button"
          aria-label="Aumentar"
          onClick={() => set(quantity + step)}
          className="flex h-12 w-12 items-center justify-center text-primary"
        >
          <PlusIcon className="h-5 w-5" />
        </button>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {chips.map(n => (
          <button
            key={n}
            type="button"
            data-testid={`wholesale-add-${n}`}
            onClick={() => set(quantity + n)}
            className="flex h-11 items-center justify-center gap-1 rounded-2xl border border-gray-300 text-sm font-bold"
          >
            <PlusIcon className="h-4 w-4" />
            {n} un.
          </button>
        ))}
      </div>

      <p className="text-xs text-gray-500">
        {family.pack_only && pack > 1
          ? `Vendido de ${pack} em ${pack} un. Dá para misturar os sabores de ${family.name}.`
          : `A faixa soma todos os sabores de ${family.name}.`}
        {unitsInCart > 0 && ` Já tem ${unitsInCart} un na sacola.`}
        {currentPrice < basePrice && ` Você paga ${formatPrice(String(currentPrice))}/un.`}
      </p>
      {missingForPack > 0 && (
        <p
          data-testid="wholesale-missing-pack"
          className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800"
        >
          Faltam {missingForPack} un para fechar a caixa de {pack}.
        </p>
      )}
    </section>
  );
}
